import React, { useReducer } from 'react';

import { LandlordOnboardingContext } from 'src/pages/landlord/onboard/context/context';

type State = {
    isUserProfileStepDone: boolean;
    isCompanyProfileStepDone: boolean;
    isForwardingEmailStepDone: boolean;
    didUserImportAnyProperties: boolean;
    isImportPropertiesStepDone: boolean;
    didUserImportAnyLeads: boolean;
    isImportLeadsStepDone: boolean;
};

const setValueInLocalStorage = (key: keyof State, value: boolean) => {
    if (!value) {
        window.localStorage.removeItem(key);
        return;
    }

    window.localStorage.setItem(key, String(value));
};

const getValueFromLocalStorage = (key: keyof State) => {
    return window.localStorage.getItem(key);
};

const reducer = (state: State, action: Partial<State>) => {
    return {
        ...state,
        ...action,
    };
};

const initialState: State = {
    isUserProfileStepDone: !!getValueFromLocalStorage('isUserProfileStepDone'),
    isCompanyProfileStepDone: !!getValueFromLocalStorage('isCompanyProfileStepDone'),
    isForwardingEmailStepDone: !!getValueFromLocalStorage('isForwardingEmailStepDone'),
    didUserImportAnyProperties: !!getValueFromLocalStorage('didUserImportAnyProperties'),
    isImportPropertiesStepDone: !!getValueFromLocalStorage('isImportPropertiesStepDone'),
    didUserImportAnyLeads: !!getValueFromLocalStorage('didUserImportAnyLeads'),
    isImportLeadsStepDone: !!getValueFromLocalStorage('isImportLeadsStepDone'),
};

type Props = {
    children: React.ReactNode;
};

export default function LandlordOnboardingProvider({ children }: Props) {
    const [state, dispatch] = useReducer(reducer, initialState);

    const setState = (key: keyof State, done: boolean) => {
        dispatch({ [key]: done });
        setValueInLocalStorage(key, done);
    };

    const getSetState = (key: keyof State) => (done: boolean) => setState(key, done);

    const resetOnboardingSteps = () => {
        dispatch(initialState);

        setValueInLocalStorage('isUserProfileStepDone', false);
        setValueInLocalStorage('isCompanyProfileStepDone', false);
        setValueInLocalStorage('isForwardingEmailStepDone', false);
        setValueInLocalStorage('didUserImportAnyProperties', false);
        setValueInLocalStorage('isImportPropertiesStepDone', false);
        setValueInLocalStorage('didUserImportAnyLeads', false);
        setValueInLocalStorage('isImportLeadsStepDone', false);
    };

    return (
        <LandlordOnboardingContext.Provider value={{
            isUserProfileStepDone: state.isUserProfileStepDone,
            setUserProfileStepDone: getSetState('isUserProfileStepDone'),

            isCompanyProfileStepDone: state.isCompanyProfileStepDone,
            setCompanyProfileStepDone: getSetState('isCompanyProfileStepDone'),

            isForwardingEmailStepDone: state.isForwardingEmailStepDone,
            setForwardingEmailStepDone: getSetState('isForwardingEmailStepDone'),

            didUserImportAnyProperties: state.didUserImportAnyProperties,
            setDidUserImportAnyProperties: getSetState('didUserImportAnyProperties'),
            isImportPropertiesStepDone: state.isImportPropertiesStepDone,
            setImportPropertiesStepDone: getSetState('isImportPropertiesStepDone'),

            didUserImportAnyLeads: state.didUserImportAnyLeads,
            setDidUserImportAnyLeads: getSetState('didUserImportAnyLeads'),
            isImportLeadsStepDone: state.isImportLeadsStepDone,
            setImportLeadsStepDone: getSetState('isImportLeadsStepDone'),

            resetOnboardingSteps,
        }}
        >
            {children}
        </LandlordOnboardingContext.Provider>
    );
}

